import { PROTOCOL_CONSTANTS } from "@klass-share/protocol";
const Z_STEP = 0x100000000n;
const Z_MAX = 0xffffffffffffffffn;
export function quantizeCanvasUnit(value) {
    return Math.round(value * 4) / 4;
}
export function prepareStrokePoints(source, minimumDistance = 0.75) {
    const points = finitePointPairs(source);
    if (points.length <= 4)
        return points.map(quantizeCanvasUnit);
    const filtered = [points[0] ?? 0, points[1] ?? 0];
    const minimumSquared = minimumDistance * minimumDistance;
    for (let index = 2; index < points.length; index += 2) {
        const previousX = filtered[filtered.length - 2] ?? 0;
        const previousY = filtered[filtered.length - 1] ?? 0;
        const x = points[index] ?? previousX;
        const y = points[index + 1] ?? previousY;
        const dx = x - previousX;
        const dy = y - previousY;
        if (dx * dx + dy * dy >= minimumSquared || index >= points.length - 2) {
            filtered.push(x, y);
        }
    }
    const maximumPoints = PROTOCOL_CONSTANTS.WB_MAX_PTS_PER_STROKE;
    const simplified = simplifyToLimit(filtered, maximumPoints);
    return simplified.map(quantizeCanvasUnit);
}
export function pointsBounds(points, padding = 0) {
    if (points.length < 2)
        return undefined;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (let index = 0; index + 1 < points.length; index += 2) {
        const x = points[index];
        const y = points[index + 1];
        if (x === undefined || y === undefined || !Number.isFinite(x + y))
            continue;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    }
    if (!Number.isFinite(minX))
        return undefined;
    return {
        x: minX - padding,
        y: minY - padding,
        width: Math.max(1, maxX - minX + padding * 2),
        height: Math.max(1, maxY - minY + padding * 2),
    };
}
export function unionRects(left, right) {
    if (left === undefined)
        return right;
    if (right === undefined)
        return left;
    const x = Math.min(left.x, right.x);
    const y = Math.min(left.y, right.y);
    const farX = Math.max(left.x + left.width, right.x + right.width);
    const farY = Math.max(left.y + left.height, right.y + right.height);
    return { x, y, width: farX - x, height: farY - y };
}
/** Fixed-width fractional positions sort lexically; concurrent ties use item id. */
export function nextFractionalIndex(values) {
    let maximum = 0n;
    for (const value of values) {
        if (!/^[0-9a-f]{16}$/u.test(value))
            continue;
        const parsed = BigInt(`0x${value}`);
        if (parsed > maximum)
            maximum = parsed;
    }
    return fractionalIndexBetween(maximum === 0n ? undefined : formatZ(maximum), undefined);
}
export function fractionalIndexBetween(left, right) {
    const leftValue = left === undefined ? 0n : parseZ(left);
    const rightValue = right === undefined ? undefined : parseZ(right);
    const next = rightValue === undefined
        ? leftValue + Z_STEP
        : leftValue + (rightValue - leftValue) / 2n;
    if (next <= leftValue ||
        next > Z_MAX ||
        (rightValue !== undefined && next >= rightValue))
        throw new Error("whiteboard fractional z-index has no remaining gap");
    return formatZ(next);
}
export function compareWhiteboardItems(left, right) {
    const zOrder = left[1].z.localeCompare(right[1].z);
    return zOrder === 0 ? left[0].localeCompare(right[0]) : zOrder;
}
function finitePointPairs(source) {
    const result = [];
    for (let index = 0; index + 1 < source.length; index += 2) {
        const x = source[index];
        const y = source[index + 1];
        if (x !== undefined && y !== undefined && Number.isFinite(x + y)) {
            result.push(x, y);
        }
    }
    return result;
}
function simplifyToLimit(points, maximumPoints) {
    const pointCount = points.length / 2;
    if (pointCount <= maximumPoints)
        return [...points];
    const result = [];
    for (let position = 0; position < maximumPoints; position += 1) {
        const sourcePoint = Math.round((position * (pointCount - 1)) / (maximumPoints - 1));
        result.push(points[sourcePoint * 2] ?? 0, points[sourcePoint * 2 + 1] ?? 0);
    }
    return result;
}
function parseZ(value) {
    if (!/^[0-9a-f]{16}$/u.test(value))
        throw new Error("whiteboard z-index must be fixed-width hexadecimal");
    return BigInt(`0x${value}`);
}
function formatZ(value) {
    return value.toString(16).padStart(16, "0");
}
//# sourceMappingURL=geometry.js.map